import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
	createProductGroup,
	createProductItem,
	fetchFields,
	fetchItemCategories,
	fetchProductById,
	fetchProducts,
	fetchUoms,
	updateProductGroup,
	updateProductItem,
	updateProductField,
	removeProductField,
	updateProductCategory,
	removeProductCategories,
} from "../API/api";
import Moment from "moment/moment";

export const getProducts = createAsyncThunk(
	"products/getProducts",
	async (_, { rejectWithValue }) => {
		try {
			const response = await fetchProducts();
			return response.data;
		} catch (err) {
			return rejectWithValue(err.response.data);
		}
	}
);

export const getUoms = createAsyncThunk(
	"products/getUoms",
	async (_, { rejectWithValue }) => {
		try {
			const response = await fetchUoms();
			return response.data;
		} catch (err) {
			return rejectWithValue(err.response.data);
		}
	}
);

export const getProductById = createAsyncThunk(
	"products/getProductById",
	async (productId, { rejectWithValue }) => {
		try {
			const response = await fetchProductById(productId);
			return response.data;
		} catch (err) {
			return rejectWithValue(err.response.data);
		}
	}
);

export const getItemCategories = createAsyncThunk(
	"products/getCategories",
	async (_, { rejectWithValue }) => {
		try {
			const response = await fetchItemCategories();
			return response.data;
		} catch (err) {
			return rejectWithValue(err.response.data);
		}
	}
);

export const getFields = createAsyncThunk(
	"products/getFields",
	async (_, { rejectWithValue }) => {
		try {
			const response = await fetchFields();
			return response.data;
		} catch (err) {
			return rejectWithValue(err.response.data);
		}
	}
);

export const postNewProductItem = createAsyncThunk(
	"products/newProductItem",
	async (newProduct, { rejectWithValue }) => {
		try {
			const response = await createProductItem(newProduct);
			return response.data;
		} catch (err) {
			return rejectWithValue(err.response.data);
		}
	}
);

export const putProductItem = createAsyncThunk(
	"products/updateProductItem",
	async (product, { rejectWithValue }) => {
		try {
			const response = await updateProductItem(product.id, product.payload);
			return response.data;
		} catch (err) {
			return rejectWithValue(err.response.data);
		}
	}
);

export const putProductCategory = createAsyncThunk(
	"products/updateProductCategories",
	async (productCategory, { rejectWithValue }) => {
		try {
			const response = await updateProductCategory(
				productCategory.id,
				productCategory.payload
			);
			return response.data;
		} catch (err) {
			return rejectWithValue(err.response.data);
		}
	}
);

export const deleteProductCategory = createAsyncThunk(
	"products/deleteProductCategory",
	async (categoryIds, { rejectWithValue }) => {
		try {
			const response = await removeProductCategories(categoryIds.payload);
			return response.data;
		} catch (err) {
			return rejectWithValue(err.response?.data || "An error occurred");
		}
	}
);

export const putProductField = createAsyncThunk(
	"products/updateProductField",
	async (productField, { rejectWithValue }) => {
		try {
			const response = await updateProductField(
				productField.id,
				productField.payload
			);
			return response.data;
		} catch (err) {
			return rejectWithValue(err.response.data);
		}
	}
);

export const deleteProductField = createAsyncThunk(
	"products/deleteProductField",
	async (fieldIds, { rejectWithValue }) => {
		try {
			const response = await removeProductField(fieldIds.payload);
			return response.data;
		} catch (err) {
			return rejectWithValue(err.response?.data || "An error occurred");
		}
	}
);

export const putProductGroup = createAsyncThunk(
	"products/updateProductGroup",
	async (product, { rejectWithValue }) => {
		try {
			const response = await updateProductGroup(product.id, product.payload);
			return response.data;
		} catch (err) {
			return rejectWithValue(err.response.data);
		}
	}
);

export const postNewProductGroup = createAsyncThunk(
	"products/newProductGroup",
	async (newProduct, { rejectWithValue }) => {
		try {
			const response = await createProductGroup(newProduct);
			return response.data;
		} catch (err) {
			return rejectWithValue(err.response.data);
		}
	}
);

const productsSlice = createSlice({
	name: "products",
	initialState: {
		getProducts: {
			status: "idle",
			array: [],
			error: null,
		},
		productGroups: {
			byId: {},
			allIds: [],
		},
		productItems: {
			byId: {},
			allIds: [],
		},
		getProductById: {
			status: "idle",
			productsById: {},
			error: null,
		},
		getItemCategories: {
			status: "idle",
			categoriesById: [],
			error: null,
		},
		putProductCategory: {
			status: "idle",
			array: [],
			error: null,
		},
		deleteProductCategory: {
			status: "idle",
			deletedCategories: [],
			error: null,
		},
		getFields: {
			status: "idle",
			array: [],
			error: null,
		},
		putProductField: {
			status: "idle",
			array: [],
			error: null,
		},
		deleteProductField: {
			status: "idle",
			deletedFields: [],
			error: null,
		},
		putProductItem: {
			status: "idle",
			error: null,
		},
		putProductGroup: {
			status: "idle",
			error: null,
		},
		createProductItem: {
			status: "idle",
			error: null,
		},
		createProductGroup: {
			status: "idle",
			error: null,
		},
		getUoms: {
			status: "idle",
			uoms: [],
			error: null,
		},
	},
	reducers: {},
	extraReducers: (builder) => {
		builder
			// Get Products
			.addCase(getProducts.pending, (state) => {
				state.getProducts.status = "loading";
			})
			.addCase(getProducts.fulfilled, (state, action) => {
				state.getProducts.status = "succeeded";

				state.getProducts.array = [
					...action.payload.productItems.map((productItem) => ({
						...productItem,
						createdAt: Moment(productItem.createdAt).format(
							"D MMM YYYY, h:mma"
						),
					})),
					...action.payload.productGroups.map((productGroup) => ({
						...productGroup,
						createdAt: Moment(productGroup.createdAt).format(
							"D MMM YYYY, h:mma"
						),
					})),
				].sort(
					(a, b) =>
						Moment(b.createdAt, "D MMM YYYY, h:mma") -
						Moment(a.createdAt, "D MMM YYYY, h:mma")
				);

				action.payload.productItems.forEach((productItem) => {
					state.productItems.byId[productItem.id] = productItem;
					if (state.productItems.allIds.includes(productItem.id)) {
						state.productItems.allIds = state.productItems.allIds.filter(
							(id) => id !== productItem.id
						);
					}
					state.productItems.allIds.push(productItem.id);
				});

				action.payload.productGroups.forEach((productGroup) => {
					state.productGroups.byId[productGroup.id] = productGroup;
					if (state.productGroups.allIds.includes(productGroup.id)) {
						state.productGroups.allIds = state.productGroups.allIds.filter(
							(id) => id !== productGroup.id
						);
					}
					state.productGroups.allIds.push(productGroup.id);
				});
			})
			.addCase(getProducts.rejected, (state, action) => {
				state.getProducts.status = "failed";
				state.getProducts.error = action.error.message;
			})
			// Get Categories
			.addCase(getItemCategories.pending, (state) => {
				state.getItemCategories.status = "loading";
			})
			.addCase(getItemCategories.fulfilled, (state, action) => {
				state.getItemCategories.status = "succeeded";
				state.getItemCategories.categoriesById = [
					...action.payload.map((category) => ({
						...category,
						createdAt: Moment(category.createdAt).format("D MMM YYYY, h:mma"),
						lastModifiedAt: category.lastModifiedAt
							? Moment(category.lastModifiedAt).format("D MMM YYYY, h:mma")
							: null,
					})),
				];
			})
			.addCase(getItemCategories.rejected, (state, action) => {
				state.getItemCategories.status = "failed";
				state.getItemCategories.error = action.error.message;
			})
			// Update Categories
			.addCase(putProductCategory.pending, (state) => {
				state.putProductCategory.status = "loading";
			})
			.addCase(putProductCategory.fulfilled, (state, action) => {
				state.putProductCategory.status = "succeeded";
				state.putProductCategory.array = action.payload;
				const existingCategory =
					state.getItemCategories.categoriesById.findIndex(
						(x) => x.id === action.payload.id
					);
				if (existingCategory !== -1) {
					state.getItemCategories.categoriesById[existingCategory] =
						action.payload;
				}
			})
			.addCase(putProductCategory.rejected, (state, action) => {
				state.putProductCategory.status = "failed";
				state.putProductCategory.error = action.error.message;
			})
			// Delete Categories
			.addCase(deleteProductCategory.pending, (state) => {
				state.deleteProductCategory.status = "loading";
			})
			.addCase(deleteProductCategory.fulfilled, (state, action) => {
				state.deleteProductCategory.status = "succeeded";
				let existingCategories = [];

				action.meta.arg.payload.ids.forEach((id) => {
					existingCategories.push(
						state.getItemCategories.categoriesById.findIndex((x) => x.id === id)
					);
				});

				if (existingCategories.length > 0) {
					state.getItemCategories.categoriesById =
						state.getItemCategories.categoriesById.filter(
							(_, index) => !existingCategories.includes(index)
						);
				}

				// Refresh products by id, as fields should no longer appear 
				state.getProductById.productsById = {};
				state.getProducts.status = 'idle';
			})
			.addCase(deleteProductCategory.rejected, (state, action) => {
				state.deleteProductCategory.status = "failed";
				state.deleteProductCategory.error = action.error.message;
			})
			// Get Fields
			.addCase(getFields.pending, (state) => {
				state.getFields.status = "loading";
			})
			.addCase(getFields.fulfilled, (state, action) => {
				state.getFields.status = "succeeded";
				state.getFields.array = action.payload;
			})
			.addCase(getFields.rejected, (state, action) => {
				state.getFields.status = "failed";
				state.getFields.error = action.error.message;
			})
			// Update Fields
			.addCase(putProductField.pending, (state) => {
				state.putProductField.status = "loading";
			})
			.addCase(putProductField.fulfilled, (state, action) => {
				state.putProductField.status = "succeeded";
				state.putProductField.array = action.payload;
				let existingField = state.getFields.array.findIndex(
					(x) => x.id === action.payload.id
				);
				if (existingField !== -1) {
					state.getFields.array[existingField] = action.payload;
				}
				
				// Refresh products, as fields should no longer appear 
				state.getProductById.productsById = {};
				state.getProducts.status = 'idle';
			})
			.addCase(putProductField.rejected, (state, action) => {
				state.putProductField.status = "failed";
				state.putProductField.error = action.error.message;
			})
			// Delete Fields
			.addCase(deleteProductField.pending, (state) => {
				state.deleteProductField.status = "loading";
			})
			.addCase(deleteProductField.fulfilled, (state, action) => {
				state.deleteProductField.status = "succeeded";
				let existingFields = [];

				action.meta.arg.payload.ids.forEach((id) => {
					existingFields.push(
						state.getFields.array.findIndex((x) => x.id === id)
					);
				});

				if (existingFields.length > 0) {
					state.getFields.array = state.getFields.array.filter(
						(_, index) => !existingFields.includes(index)
					);
				}
				
				// Refresh products, as fields should no longer appear 
				state.getProductById.productsById = {};
				state.getProducts.status = 'idle';
			})
			.addCase(deleteProductField.rejected, (state, action) => {
				state.deleteProductField.status = "failed";
				state.deleteProductField.error = action.error.message;
			})
			// Get Product by Id
			.addCase(getProductById.pending, (state) => {
				state.getProductById.status = "loading";
			})
			.addCase(getProductById.fulfilled, (state, action) => {
				state.getProductById.status = "succeeded";
				state.getProductById.productsById[action.payload.id] = action.payload;
			})
			.addCase(getProductById.rejected, (state, action) => {
				state.getProductById.status = "failed";
				state.getProductById.error = action.error.message;
			})
			// Create Product Item
			.addCase(postNewProductItem.pending, (state) => {
				state.createProductItem.status = "loading";
			})
			.addCase(postNewProductItem.fulfilled, (state) => {
				state.createProductItem.status = "succeeded";
				// force a refresh
				state.getProducts.status = "idle";
				state.getItemCategories.status = "idle";
				state.getFields.status = "idle";
			})
			.addCase(postNewProductItem.rejected, (state, action) => {
				state.createProductItem.status = "failed";
				state.createProductItem.error = action.error.message;
			})
			// Update Product Item
			.addCase(putProductItem.pending, (state) => {
				state.putProductItem.status = "loading";
			})
			.addCase(putProductItem.fulfilled, (state, action) => {
				state.getProductById.productsById[action.payload.id] =
					action.payload;
				state.putProductItem.status = "succeeded";
				state.getProducts.status = "idle";
				state.getItemCategories.status = "idle";
				state.getFields.status = "idle";
			})
			.addCase(putProductItem.rejected, (state, action) => {
				state.putProductItem.status = "failed";
				state.putProductItem.error = action.error.message;
			})
			// Create Product Group
			.addCase(postNewProductGroup.pending, (state) => {
				state.createProductGroup.status = "loading";
			})
			.addCase(postNewProductGroup.fulfilled, (state) => {
				state.createProductGroup.status = "succeeded";
				// force a refresh
				state.getProducts.status = "idle";
				state.getItemCategories.status = "idle";
			})
			.addCase(postNewProductGroup.rejected, (state, action) => {
				state.createProductGroup.status = "failed";
				state.createProductGroup.error = action.error.message;
			})
			// Update Product Group
			.addCase(putProductGroup.pending, (state) => {
				state.putProductGroup.status = "loading";
			})
			.addCase(putProductGroup.fulfilled, (state, action) => {
				state.putProductGroup.status = "succeeded";
				state.getProductById.productsById[action.payload.id] =
					action.payload;

				state.getProducts.status = "idle";
				state.getItemCategories.status = "idle";
			})
			.addCase(putProductGroup.rejected, (state, action) => {
				state.putProductGroup.status = "failed";
				state.putProductGroup.error = action.error.message;
			})
			// Get UOMs
			.addCase(getUoms.pending, (state) => {
				state.getUoms.status = "loading";
			})
			.addCase(getUoms.fulfilled, (state, action) => {
				state.getUoms.status = "succeeded";
				state.getUoms.uoms = action.payload.data;
			})
			.addCase(getUoms.rejected, (state, action) => {
				state.getUoms.status = "failed";
				state.getUoms.error = action.error.message;
			});
	},
});

export const selectGetAllProductItemsAllIds = (state) =>
	state.products.productItems.allIds;
export const selectGetAllProductItemsById = (state) =>
	state.products.productItems.byId;
export const selectGetAllProductsArray = (state) =>
	state.products.getProducts.array;
export const selectGetAllProductGroupsAllIds = (state) =>
	state.products.productGroups.allIds;
export const selectGetAllProductGroupsById = (state) =>
	state.products.productGroups.byId;
export const getAllProductsStatus = (state) =>
	state.products.getProducts.status;
export const getAllProductsError = (state) => state.products.getProducts.error;

export const getCategoriesStatus = (state) =>
	state.products.getItemCategories.status;
export const selectGetCategoriesById = (state) =>
	state.products.getItemCategories.categoriesById;

export const getFieldsStatus = (state) => state.products.getFields.status;
export const getFieldsArray = (state) => state.products.getFields.array;

export const selectGetProductsById = (state) =>
	state.products.getProductById.productsById;
export const getUomsStatus = (state) => state.products.getUoms.status;
export const getUomsData = (state) => state.products.getUoms.uoms;

export default productsSlice.reducer;
